import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// custom
import { usePageLayout } from '@/app/router';
import { ROUTES } from '@/app/router/routes';
import { post82DnaCalculate, post83DnaRestart } from '../api/dnaService';
import Dna11FullX2 from '@shared/assets/dna/Dna11_full_x2.jpg';
import Dna12FullX2 from '@shared/assets/dna/Dna12_full_x2.jpg';
import Dna13FullX2 from '@shared/assets/dna/Dna13_full_x2.jpg';
import Dna14FullX2 from '@shared/assets/dna/Dna14_full_x2.jpg';
import Dna15FullX2 from '@shared/assets/dna/Dna15_full_x2.jpg';
import Dna16FullX2 from '@shared/assets/dna/Dna16_full_x2.jpg';
import Dna11FullX2En from '@shared/assets/dna/Dna11_full_x2_en.png';
import Dna12FullX2En from '@shared/assets/dna/Dna12_full_x2_en.png';
import Dna13FullX2En from '@shared/assets/dna/Dna13_full_x2_en.png';
import Dna14FullX2En from '@shared/assets/dna/Dna14_full_x2_en.png';
import Dna15FullX2En from '@shared/assets/dna/Dna15_full_x2_en.png';
import Dna16FullX2En from '@shared/assets/dna/Dna16_full_x2_en.png';
import { DnaCloseButton } from './DnaListPage';
import { useLanguage } from '@shared/contexts/LanguageContext';

/** 與 mock i82Calculate / API 回傳相容 */
function normalizeCalculatePayload(raw) {
  const body = raw?.data ?? raw ?? {};
  return {
    name: body.name ?? '',
    title: body.title ?? '',
    description: body.description ?? '',
    blessing: body.blessing ?? '',
    teammate: body.teammate ?? '',
    message: body.message,
    resultCode: body.result_code,
  };
}

const dnaImageMap = {
  "蒼穹": Dna11FullX2,
  "暖陽": Dna12FullX2,
  "行雲": Dna13FullX2,
  "大地": Dna14FullX2,
  "磐石": Dna15FullX2,
  "岩石": Dna15FullX2,
  "川流": Dna16FullX2,
};

const dnaImageMapEn = {
  "蒼穹": Dna11FullX2En,
  "暖陽": Dna12FullX2En,
  "行雲": Dna13FullX2En,
  "大地": Dna14FullX2En,
  "磐石": Dna15FullX2En,
  "岩石": Dna15FullX2En,
  "川流": Dna16FullX2En,
};

//---------------------------------------------------------------------------
const DnaAnswerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setLayoutOptions } = usePageLayout();
  const { t, currentLanguage } = useLanguage();
  const isEn = currentLanguage.code === 'en';

  const recordIdFromState = location.state?.recordId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [restarting, setRestarting] = useState(false);

  useEffect(() => {
    setLayoutOptions({ hideHeader: true, hideBottomNav: true, hideFooter: true });
    return () => setLayoutOptions({});
  }, [setLayoutOptions]);

  useEffect(() => {
    if (recordIdFromState == null) {
      setLoading(false);
      setError(new Error(t('dna-answer.missingRecord')));
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setResult(null);

    post82DnaCalculate(recordIdFromState)
      .then((res) => {
        if (cancelled) return;
        setResult(normalizeCalculatePayload(res));
      })
      .catch((e) => {
        if (cancelled) return;
        console.error(e);
        setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [recordIdFromState]);

  //---------------------------------------------------------------------------
  function onClickBack() {
    if (location.state?.fromPage === 'profile') {
      navigate(-1);
    } else {
      navigate(-2);
    }
  };
  
  const handleRestart = useCallback(async () => {
    setRestarting(true);
    setError(null);
    try {
      await post83DnaRestart();
      navigate(ROUTES.DNA_QUESTION, { replace: true });
    } catch (e) {
      console.error(e);
      setError(e);
    } finally {
      setRestarting(false);
    }
  }, [navigate]);


  //---------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 text-[#6E625B]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <p className="text-sm">{t('dna-answer.calculating')}</p>
      </div>
    );
  }

  if (error || !result) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-sm text-[#6E625B]">
          {recordIdFromState == null ? t('dna-answer.enterFromFlow') : t('dna-answer.fetchError')}
        </p>
        <button
          type="button"
          onClick={() => navigate(ROUTES.DNA_QUESTION, { replace: true })}
          className="rounded-xl bg-primary px-6 py-2.5 text-sm font-medium text-white"
        >
          {t('dna-answer.backToQuiz')}
        </button>
      </div>
    );
  }

  const dnaImage = (isEn ? dnaImageMapEn[result.name] : null) ?? dnaImageMap[result.name];

  //---------------------------------------------------------------------------
  return (
    <div className="mx-auto flex w-full max-w-[430px] flex-col items-center gap-4">

      {dnaImage ? (
        <div className="relative w-full max-w-[430px] overflow-hidden aspect-430/932">
          <img
            src={dnaImage}
            alt={`${result.name} ${t('dna-answer.imageAlt')}`}
            className="h-full w-full object-contain"
          />
          <DnaCloseButton onClick={onClickBack} />
        </div>
      ) : (
        <div className="flex min-h-[50vh] w-full flex-col items-center justify-center border border-[#E8DCC8] bg-buddha-cream px-4 text-center text-sm text-[#6E625B]">
          {t('dna-answer.imageNotFound').replace('{{name}}', result.name)}
        </div>
      )}
    </div>
  );
};

export default DnaAnswerPage;
